import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronDown,
  Search,
  Clock,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useVisibleRecentSearches } from '../../hooks/useVisibleRecentSearches';
import { getRecentSearches } from '../../services/api';
import { serverRecentToLocal } from '../../utils/recentSearchStorage';
import { useState, useEffect } from 'react';
import type { RecentSearch } from '../../types';

interface RecentActivityPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateBack?: () => void;
}

export function RecentActivityPanel({ isOpen, onClose, onNavigateBack }: RecentActivityPanelProps) {
  const recentSearches = useVisibleRecentSearches();
  const { isAuthenticated } = useAuthStore();
  const searchHistorySync = useSettingsStore((s) => s.settings.searchHistory);
  const [serverSearches, setServerSearches] = useState<RecentSearch[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !isAuthenticated || !searchHistorySync) {
      setServerSearches([]);
      return;
    }
    getRecentSearches(20).then((remote) => {
      setServerSearches(remote.map(serverRecentToLocal));
    }).catch(() => { });
  }, [isOpen, isAuthenticated, searchHistorySync]);

  // Merge local + server, deduplicate by query, sort newest first
  const merged: RecentSearch[] = [...recentSearches];
  for (const s of serverSearches) {
    if (!merged.some((r) => r.query === s.query)) merged.push(s);
  }
  merged.sort((a, b) => (b.timestamp as number) - (a.timestamp as number));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed inset-0 bg-white dark:bg-gray-900 z-[90] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-neutral-700 pt-safe">
          <button
            type="button"
            onClick={onNavigateBack ?? onClose}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <ChevronLeft size={22} className="text-gray-700 dark:text-neutral-300" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
            Recent Activity
          </h2>
          <div className="w-[38px]" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {merged.length === 0 ? (
            <div className="text-center py-20 px-4">
              <Clock size={40} className="mx-auto text-gray-300 dark:text-neutral-600 mb-3" />
              <p className="text-gray-500 dark:text-neutral-400 text-sm">No recent activity yet</p>
              <p className="text-gray-400 dark:text-neutral-500 text-xs mt-1">
                Your searches and navigation history will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-neutral-800">
              {merged.slice(0, 20).map((search) => {
                const isExpanded = expandedId === search.id;

                return (
                  <div key={search.id}>
                    {/* Summary row */}
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : search.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${isExpanded
                        ? 'bg-gray-50 dark:bg-neutral-800/50'
                        : 'hover:bg-gray-50 dark:hover:bg-neutral-800/50'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
                          <Search size={16} className="text-gray-400 dark:text-neutral-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {search.query}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-neutral-500">
                            {new Date(search.timestamp).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`text-gray-400 dark:text-neutral-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''
                            }`}
                        />
                      </div>
                    </button>

                    {/* Expanded detail */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-3 border-l-2 border-gray-200 dark:border-neutral-700 ml-[23px] pl-[37px] pr-4">
                            {search.result?.name && search.result.name !== search.query && (
                              <p className="text-xs text-gray-500 dark:text-neutral-400 pt-1">
                                {search.result.name}
                              </p>
                            )}
                            <p className="text-[10px] text-gray-400 dark:text-neutral-500 mt-1">
                              {new Date(search.timestamp).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
